import { InputText as PrimereactInputText} from "primereact/inputtext";
import { OverlayPanel } from 'primereact/overlaypanel';
import { Checkbox } from 'primereact/checkbox';
import { ReactComponent as SortAscIcon } from '../../assets/images/datatable/sort-asc.svg';
import { ReactComponent as SortDescIcon } from '../../assets/images/datatable/sort-desc.svg';
import { ReactComponent as SearchIcon } from '../../assets/images/search-icon.svg';
import { Button } from "primereact/button";
import { Slider } from "primereact/slider";
import { useEffect, useMemo, useRef, useState } from "react";
import { FILTER_TYPES } from "../datatable/DataTable";
import { useTranslation } from "react-i18next";
import './FilterPanel.scss';
import Calendar from "../input/Calendar";
import InputText from "../input/InputText";
import InputAmount from "../input/InputAmount";
import { VirtualScroller } from "primereact/virtualscroller";

const FilterPanel = ({ref, onSortAsc, onSortDesc, type, params, applyFilter, timestamp}) => {

    const SLIDER_MAX_VALUE_DEFAULUT = 1000;
    
    const { t } = useTranslation();
    const searchRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [booleanValue, setBooleanValue] = useState(null);
    const [list, setList] = useState(type === FILTER_TYPES.LIST ?  params : []);
    const [dateRange, setDateRange] = useState({from: null, to: null});
    const [numberRange, setNumberRange] = useState({from: null, to: null, maxValue: SLIDER_MAX_VALUE_DEFAULUT});
    const [amountRange, setAmountRange] = useState({from: null, to: null, maxValue: SLIDER_MAX_VALUE_DEFAULUT});
    const [sliderValue, setSliderValue] = useState([0, SLIDER_MAX_VALUE_DEFAULUT]);
    
    useEffect(() => {
        if (type === FILTER_TYPES.LIST) {
            setList(params.map(item => ({...item, hidden: false})));
            setSearchQuery('');
            setDateRange({});
            setNumberRange({});
        } else if (type === FILTER_TYPES.TEXT) {
            setList([]);
            // Upewnij się, że params jest stringiem
            const textValue = typeof params === 'string' ? params : (params?.value || '');
            setSearchQuery(textValue);            
            setDateRange({});
            setNumberRange({});
        } else if (type === FILTER_TYPES.BOOLEAN) {
            setBooleanValue(params !== undefined && params !== null ? params : null);
            setList([]);
            setSearchQuery('');
            setDateRange({});
            setNumberRange({});
        } else if (type === FILTER_TYPES.DATE_RANGE) {
            setList([]);
            setSearchQuery('');
            setDateRange({...params});
            setNumberRange({});
        } else if (type === FILTER_TYPES.DATE_TIME_RANGE) {            
            setList([]);
            setSearchQuery('');
            setDateRange({...params});
            setNumberRange({});
        } else if (type === FILTER_TYPES.NUMBER_RANGE) {            
            setList([]);
            setSearchQuery('');
            setDateRange({});
            
            
            // Wartości są w params.value, nie bezpośrednio w params
            const value = params?.value || params;
            
            setNumberRange({
                from: value?.from !== null && value?.from !== undefined ? value.from : null,
                to: value?.to !== null && value?.to !== undefined ? value.to : null,
                maxValue: value?.maxValue || SLIDER_MAX_VALUE_DEFAULUT
            });
            
            const from = value?.from !== null && value?.from !== undefined ? value.from : 0;
            const to = value?.to !== null && value?.to !== undefined ? value.to : (value?.maxValue || SLIDER_MAX_VALUE_DEFAULUT);
            setSliderValue([from, to]);
        } else if (type === FILTER_TYPES.AMOUNT) {            
            setList([]);
            setSearchQuery('');
            setDateRange({});
            setNumberRange({});
            
            // Debugowanie - sprawdź co zawiera params
            //console.log('AMOUNT params:', params);
            
            // Wartości są w params.value, nie bezpośrednio w params
            const value = params?.value || params;
            
            setAmountRange({
                from: value?.from !== null && value?.from !== undefined ? value.from : null,
                to: value?.to !== null && value?.to !== undefined ? value.to : null,
                maxValue: value?.maxValue || SLIDER_MAX_VALUE_DEFAULUT
            });
        }
    }, [type, params, timestamp]);

    // Synchronizuj sliderValue z numberRange dla NUMBER_RANGE
    useEffect(() => {
        if (type === FILTER_TYPES.NUMBER_RANGE) {
            const max = numberRange.maxValue || SLIDER_MAX_VALUE_DEFAULUT;
            const from = Math.max(0, Math.min(numberRange.from || 0, max));
            const to = Math.max(0, Math.min(numberRange.to || max, max));
            if (sliderValue[0] !== from || sliderValue[1] !== to) {
                setSliderValue([from, to]);
            }
        }
    }, [numberRange, type]);

    // Dodatkowa synchronizacja dla NUMBER_RANGE - upewnij się, że wartości są prawidłowo zainicjalizowane
    useEffect(() => {
        if (type === FILTER_TYPES.NUMBER_RANGE && params) {
            // Wartości są w params.value, nie bezpośrednio w params
            const value = params?.value || params;
            
            // Jeśli params zawiera wartości, ale numberRange jest pusty, zaktualizuj numberRange
            if ((value?.from !== null && value?.from !== undefined) && (numberRange.from === null || numberRange.from === undefined)) {
                setNumberRange(prev => ({
                    ...prev,
                    from: value.from
                }));
            }
            if ((value?.to !== null && value?.to !== undefined) && (numberRange.to === null || numberRange.to === undefined)) {
                setNumberRange(prev => ({
                    ...prev,
                    to: value.to
                }));
            }
        }
    }, [type, params, numberRange.from, numberRange.to]);

    useEffect(() => {
        if (type === FILTER_TYPES.LIST) {
            filterList();
        }
    }, [searchQuery]);

    
    const filterList = () => {
        if (type === FILTER_TYPES.LIST) {            
            let newList = list?.map(item => ({...item, hidden: false}));
            if (searchQuery && searchQuery.trim() !== '') {
                const query = searchQuery.trim().toUpperCase();
                newList = newList.map(item => ({...item, hidden: !item.label.toUpperCase().includes(query)}));
            }
            setList(newList);
        }        
    }

    const applyFilterImpl = () => {
        if (applyFilter) {
            applyFilter(type, 
                type === FILTER_TYPES.LIST ? list.filter(item => item.checked).map(item => ({id: item.id, label: item.label})) : 
                type === FILTER_TYPES.TEXT ? searchQuery : 
                type === FILTER_TYPES.DATE_RANGE ? dateRange: 
                type === FILTER_TYPES.DATE_TIME_RANGE ? dateRange: 
                type === FILTER_TYPES.NUMBER_RANGE ? numberRange : 
                type === FILTER_TYPES.AMOUNT ? amountRange : 
                type === FILTER_TYPES.BOOLEAN ? (booleanValue === null || booleanValue === undefined ? null : booleanValue) : null
            );
            ref.current.hide();
        }
    }

    const onShowPanel = () => {
        if (type === FILTER_TYPES.TEXT) {
            searchRef.current.focus();
        }
    }

    

    const visibleList = list?.filter(item => item.hidden !== true);

    const ITEM_SIZE = 38; // Rzeczywista wysokość jednego elementu (z paddingiem)
    const MAX_VISIBLE_ITEMS = 5;
    
    const scrollerHeight = useMemo(() => {
        const itemCount = visibleList?.length || 0;
        const visibleCount = Math.min(itemCount, MAX_VISIBLE_ITEMS);
        return visibleCount * ITEM_SIZE;
    }, [visibleList?.length]);
    

    return (
        <OverlayPanel ref={ref} closeOnEscape dismissable={true} unstyled className='filter-panel' onShow={onShowPanel}>
            <div className={"filter-panel-content"}>
                {onSortAsc && onSortDesc && (
                    <>
                        <div className="filter-panel-sort p-2">
                            <div className="item" onClick={e => onSortAsc()}>
                                <SortAscIcon className="sort-icon" />
                                <div className="item-text">{t('filterPanel.sortAsc')}</div>
                            </div>
                            <div className="item" onClick={e => onSortDesc()}>
                                <SortDescIcon className="sort-icon" />
                                <div className="item-text">{t('filterPanel.sortDesc')}</div>
                            </div>
                        </div>
                        <div className='separator' />
                    </>
                )}

                {type === FILTER_TYPES.LIST && (
                    <div className='w-full p-2'>
                        {list?.length > 5 && <span className="p-input-icon-left w-full">
                            <PrimereactInputText value={searchQuery} className='search-input small' placeholder={t('filterPanel.search')} 
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            <SearchIcon />
                        </span>}

                        <div className="content-list">
                            {visibleList && visibleList.length > 0 ? (
                                <VirtualScroller 
                                    items={visibleList}
                                    itemSize={ITEM_SIZE}
                                    itemTemplate={(item, options) => (
                                        <div className="item" key={`checkbox-${item.id}`} style={{ height: `${ITEM_SIZE}px` }}>
                                            <Checkbox checked={item.checked} onChange={e => {
                                                setList(list => list.map(i => i === item ? {...i, checked: e.checked} : i));
                                            }}/>
                                            <div className="item-text">{item.label}</div>
                                        </div>
                                    )}
                                    style={{ width: '100%', height: `${scrollerHeight}px` }}
                                />
                            ) : null}
                            {/* {list?.filter(item => item.hidden !== true).map((item, index) => (
                                <div className="item" key={`checkbox-${index}`}>
                                    <Checkbox checked={item.checked} onChange={e => {
                                        setList(list => list.map(i => i === item ? {...i, checked: e.checked} : i));
                                    }}/>
                                    <div className="item-text">{item.label}</div>
                                </div>
                            ))} */}
                            
                        </div>
                    </div>
                )}
                {type === FILTER_TYPES.TEXT && (
                    <div className='w-full p-2'>
                        <span className="p-input-icon-left w-full">
                            <PrimereactInputText value={searchQuery} className='search-input small' placeholder={t('filterPanel.search')}
                                onChange={e => setSearchQuery(e.target.value)} ref={searchRef}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        applyFilterImpl();
                                    }
                                }}
                            />
                            <SearchIcon />
                        </span>
                    </div>
                )}
                {type === FILTER_TYPES.DATE_RANGE && (
                    <div className='w-full p-2 flex gap-2'>
                            <Calendar id='dateRangeFrom' value={dateRange.from} inputClassName='small' label={t('filterPanel.dateFrom')}
                                minDate={params?.minDate}
                                maxDate={params?.maxDate}
                                viewDate={params?.minDate}
                                onChange={e => {
                                    setDateRange(prev => ({
                                        ...prev,
                                        from: e.value
                                    }))
                                }}
                            />
                        <div className='field'>
                            <Calendar id='dateRangeTo' value={dateRange.to} inputClassName='small' label={t('filterPanel.dateTo')}
                                minDate={params?.minDate}
                                maxDate={params?.maxDate}
                                viewDate={params?.maxDate}
                                onChange={e => {
                                    setDateRange(prev => ({
                                        ...prev,
                                        to: e.value
                                    }))
                                }}
                            />
                        </div>
                    </div>
                )}
                {type === FILTER_TYPES.DATE_TIME_RANGE && (
                    <div className='w-full p-2 flex gap-2'>
                            <Calendar id='dateTimeRangeFrom' value={dateRange.from} inputClassName='small' label={t('filterPanel.dateFrom')} 
                                showTime={true}
                                onChange={e => {
                                    setDateRange(prev => ({
                                        ...prev,
                                        from: e.value
                                    }))
                                }}
                            />
                        <div className='field'>
                            <Calendar id='dateTimeRangeTo' value={dateRange.to} inputClassName='small' label={t('filterPanel.dateTo')}
                                showTime={true}
                                onChange={e => {
                                    setDateRange(prev => ({
                                        ...prev,
                                        to: e.value
                                    }))
                                }}
                            />
                        </div>
                    </div>
                )}
                {type === FILTER_TYPES.NUMBER_RANGE && (
                    <div className='w-full p-2'>
                        <div className='mb-3 flex gap-2'>
                            <InputText 
                                id='numberRangeFrom' 
                                keyfilter="pint" 
                                inputClassName='small'  
                                label={t('filterPanel.numberFrom')}
                                value={numberRange.from ?? ''}
                                onChange={e => {
                                    let value = e.target.value !== '' ? parseInt(e.target.value) : null;
                                    // Ogranicz wartość do zakresu 0-maxValue
                                    if (value !== null) {
                                        value = Math.max(0, Math.min(value, numberRange.maxValue || SLIDER_MAX_VALUE_DEFAULUT));
                                    }
                                    setNumberRange(prev => {
                                        const newFrom = value;
                                        const newTo = prev.to !== null && newFrom !== null && newFrom > prev.to ? newFrom : prev.to;
                                        return {
                                        ...prev,
                                            from: newFrom,
                                            to: newTo
                                        };
                                    });
                                }}
                            />
                        <div className='field'>
                                <InputText 
                                    id='numberRangeTo' 
                                    keyfilter="pint" 
                                    inputClassName='small' 
                                    label={t('filterPanel.numberTo')} 
                                    value={numberRange.to ?? ''} 
                                onChange={e => {
                                        let value = e.target.value !== '' ? parseInt(e.target.value) : null;
                                        // Ogranicz wartość do zakresu 0-maxValue
                                        if (value !== null) {
                                            value = Math.max(0, Math.min(value, numberRange.maxValue || SLIDER_MAX_VALUE_DEFAULUT));
                                        }
                                        setNumberRange(prev => {
                                            const newTo = value;
                                            const newFrom = prev.from !== null && newTo !== null && newTo < prev.from ? newTo : prev.from;
                                            return {
                                                ...prev,
                                                from: newFrom,
                                                to: newTo
                                            };
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className='mb-3'>
                            {/* <label className='block text-sm font-medium mb-2'>{t('filterPanel.numberRange')}</label> */}
                            <Slider 
                                value={sliderValue} 
                                onChange={(e) => {
                                    const [from, to] = e.value;
                                    // Upewnij się, że from <= to
                                    const validFrom = Math.min(from, to);
                                    const validTo = Math.max(from, to);
                                    setSliderValue([validFrom, validTo]);
                                    setNumberRange(prev => ({
                                        ...prev,
                                        from: validFrom,
                                        to: validTo
                                    }));
                                }}
                                range 
                                min={0} 
                                max={numberRange?.maxValue || SLIDER_MAX_VALUE_DEFAULUT}
                                step={1}
                                className='w-full'
                            />
                        </div>
                    </div>
                )}
                {type === FILTER_TYPES.AMOUNT && (
                    <div className='w-full p-2'>
                        <div className='mb-3 flex gap-2'>
                            <InputAmount 
                                id='amountRangeFrom' 
                                inputClassName='w-full small p-inputtext p-component'
                                label={t('filterPanel.amountFrom')}
                                value={amountRange.from}
                                maxValue={amountRange.maxValue || SLIDER_MAX_VALUE_DEFAULUT}
                                onChange={e => {
                                    const value = e.target.value;
                                    setAmountRange(prev => ({
                                        ...prev,
                                        from: value,
                                        to: prev.to !== null && value > prev.to ? value : prev.to
                                    }));
                                }}
                            />
                            <div className='field'>
                                <InputAmount 
                                    id='amountRangeTo w-full' 
                                    inputClassName='w-full small p-inputtext p-component'
                                    label={t('filterPanel.amountTo')} 
                                    value={amountRange.to}
                                    maxValue={amountRange.maxValue || SLIDER_MAX_VALUE_DEFAULUT}
                                    onChange={e => {
                                        const value = e.target.value;
                                        setAmountRange(prev => ({
                                            ...prev,
                                            to: value,
                                            from: prev.from !== null && value < prev.from ? value : prev.from
                                        }));
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {type === FILTER_TYPES.BOOLEAN && (
                    <div className='w-full p-2 flex flex-column gap-2'>
                        <div className='flex align-items-center'>
                            <Checkbox inputId='boolean-true' name='boolean-filter' checked={booleanValue === true} onChange={(e) => {
                                // Always set to true when clicking on "Tak", even if already checked
                                setBooleanValue(true);
                            }} />
                            <label htmlFor='boolean-true' className='ml-2 cursor-pointer'>{t('Boolean.true')}</label>
                        </div>
                        <div className='flex align-items-center'>
                            <Checkbox inputId='boolean-false' name='boolean-filter' checked={booleanValue === false} onChange={(e) => {
                                // Always set to false when clicking on "Nie", even if already checked
                                setBooleanValue(false);
                            }} />
                            <label htmlFor='boolean-false' className='ml-2 cursor-pointer'>{t('Boolean.false')}</label>
                        </div>
                    </div>
                )}
                <Button label={t('filterPanel.apply')} className='small w-full' onClick={e => applyFilterImpl()}/>
            </div>
        </OverlayPanel>
    )
}

export default FilterPanel;